import { Trans } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { type ReactNode, useEffect, useMemo } from "react";

import { ApiConnector } from "../../../api/api-connector";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { SelectionMode } from "../../../models/data-model";
import { MedData, MedDataItem } from "../common/med-data";
import { Patient } from "../patient";

export const BasicMedicalTable = observer(function BasicMedicalTable(props: {
  data: MedDataItem[];
  api: ApiConnector;
  patient: Patient;
  onInvalidated?: () => void;
  disableEdit?: boolean;
  disableHistory?: boolean;
  disablePagination?: boolean;
  persistState?: boolean;
  canSelect?: SelectionMode;
  onSelect?: (selected: string[]) => void;
  preselected?: string[];
  filterActions?: ReactNode;
}) {
  const userUUID = props.api.requireUserUUID();
  const model = useMemo(
    () => new MedData<MedDataItem>([], props.api.permissions, userUUID),
    [props.api.permissions, userUUID],
  );

  if (props.disableHistory) {
    model.hasHistory = false;
  }

  // Update data when props.data changes
  useEffect(() => {
    model.updateData(props.data);
  }, [model, props.data]);

  return (
    <DataTable
      filterActions={props.filterActions}
      persistState={props.persistState}
      editMode="dialog"
      model={model}
      itemKeyField="uuid"
      columns={[
        {
          header: <Trans>Measured</Trans>,
          attr: "measurement_datetime",
          type: "datetime",
          editable: true,
          sort: true,
          initSort: "desc",
        },
        {
          header: <Trans>Type</Trans>,
          attr: "measurement_type",
          type: "string",
          sort: true,
        },
        {
          header: <Trans>Value</Trans>,
          attr: "measurement_value",
          type: "number",
          precision: 1,
          editable: true,
        },
        {
          header: <Trans>Unit</Trans>,
          attr: "measurement_unit",
          type: "string",
        },
        {
          header: <Trans>Comment</Trans>,
          attr: "comment",
          type: "string",
          multiline: true,
          editable: true,
        },
        model.showingHistory && {
          header: <Trans>Added</Trans>,
          attr: "created_at",
          type: "datetime",
        },
        model.showingHistory && {
          header: <Trans>Last edit</Trans>,
          cell: ({ item }) => <CellLastEdit item={item} />,
        },
      ]}
      onEdit={
        props.disableEdit
          ? undefined
          : async (editedField, item) => {
              await props.patient.updateItem(
                "medicaldata",
                item.uuid,
                editedField,
              );
              props.onInvalidated?.();
            }
      }
      onDelete={
        props.disableEdit
          ? undefined
          : async (item) => {
              await props.patient.deleteItem("medicaldata", item.uuid);
              props.onInvalidated?.();
            }
      }
      rowsPerPage={props.disablePagination ? Number.MAX_VALUE : 25}
      canSelect={props.canSelect}
      onSelect={props.onSelect}
      preselected={props.preselected}
    />
  );
});
